import { COMPILED_ROLE_PERMISSIONS, formatScopeKey } from './rbac-contracts.js';
import type {
  AccessExplanation,
  AccessRequest,
  CommandResult,
  DiagnosticTraceStep,
  EvaluationResult,
  Permission,
  PermissionScope,
  PolicyChange,
  PublicKeyResolver,
} from './rbac-contracts.js';
import { ControlPlaneJournal } from './control-plane-journal.js';

export interface EvaluatorDependencies {
  journal: ControlPlaneJournal;
  keyResolver: PublicKeyResolver;
  meshId: string;
}

interface CachedEvaluation {
  result: EvaluationResult;
  policyVersion: number;
  validUntilMs: number;
}

export class ScopedRbacEvaluator {
  private readonly decisionCache = new Map<string, CachedEvaluation>();

  constructor(private readonly deps: EvaluatorDependencies) {
    this.deps.journal.onEnvelopeCommitted(() => {
      this.decisionCache.clear();
    });
  }

  evaluateAccess(request: AccessRequest): EvaluationResult {
    if (request.meshId !== this.deps.meshId) {
      return {
        allowed: false,
        reason: 'UNAUTHENTICATED',
        evaluatedAtStep: 1,
        policyVersion: this.deps.journal.getPolicyVersion(),
      };
    }

    const scopeKey = formatScopeKey(request.scope);
    const cacheKey = `${request.meshId}|${request.subjectId}|${request.permission}|${scopeKey}`;
    const cached = this.decisionCache.get(cacheKey);

    if (
      cached &&
      cached.policyVersion === this.deps.journal.getPolicyVersion() &&
      request.nowMs < cached.validUntilMs &&
      !this.deps.keyResolver.isKeyRevoked(request.subjectId, request.nowMs)
    ) {
      return cached.result;
    }

    const explanation = this.explainAccess(request);
    const result: EvaluationResult = {
      allowed: explanation.allowed,
      reason: explanation.reason,
      evaluatedAtStep: explanation.trace[explanation.trace.length - 1]?.step ?? 6,
      policyVersion: explanation.policyVersion,
    };

    this.decisionCache.set(cacheKey, {
      result,
      policyVersion: explanation.policyVersion,
      validUntilMs: request.nowMs + 30_000, // 30s TTL
    });

    return result;
  }

  explainAccess(request: AccessRequest): AccessExplanation {
    const trace: DiagnosticTraceStep[] = [];
    const policyVersion = this.deps.journal.getPolicyVersion();
    const scopeKey = formatScopeKey(request.scope);

    // Step 1: Principal Integrity & Revocation Check
    if (!request.subjectId || request.subjectId.trim() === '') {
      trace.push({ step: 1, name: 'Principal Integrity', decision: 'DENY', details: 'Subject ID is empty' });
      return { allowed: false, reason: 'UNAUTHENTICATED', policyVersion, trace };
    }

    if (this.deps.keyResolver.isKeyRevoked(request.subjectId, request.nowMs)) {
      trace.push({ step: 1, name: 'Principal Revocation', decision: 'DENY', details: 'Subject key is revoked' });
      return { allowed: false, reason: 'REVOKED', policyVersion, trace };
    }
    trace.push({ step: 1, name: 'Principal Integrity', decision: 'NEUTRAL', details: 'Subject authenticated and valid' });

    // Step 1.5: Subject Isolation Check for user.self
    if (request.permission === 'user.self') {
      const isSelfScope = scopeKey === 'system' || scopeKey === `subject:${request.subjectId}` || scopeKey === `room:subject:${request.subjectId}`;
      if (!isSelfScope) {
        trace.push({ step: 1, name: 'Subject Isolation', decision: 'DENY', details: 'user.self restricted to authenticated self profile scope' });
        return { allowed: false, reason: 'PRIVILEGE_ESCALATION_DENIED', policyVersion, trace };
      }
    }

    // Step 2: Active Explicit Deny Restrictions
    const restriction = this.deps.journal.findActiveRestriction(
      request.subjectId,
      request.permission,
      scopeKey,
      request.nowMs
    );
    if (restriction) {
      trace.push({
        step: 2,
        name: 'Explicit Deny Restriction',
        decision: 'DENY',
        details: `Blocked by restriction ${restriction.id} (${restriction.reason})`,
      });
      return { allowed: false, reason: 'EXPLICIT_DENY', policyVersion, trace };
    }
    trace.push({ step: 2, name: 'Explicit Deny Restriction', decision: 'NEUTRAL', details: 'No active restrictions found' });

    // Step 3: Direct Explicit Grants / Revocations
    const directGrant = this.deps.journal.findDirectGrant(
      request.subjectId,
      request.permission,
      scopeKey,
      request.nowMs
    );
    if (directGrant) {
      if (directGrant.effect === 'deny') {
        trace.push({ step: 3, name: 'Direct Grant Policy', decision: 'DENY', details: 'Explicit deny grant matched' });
        return { allowed: false, reason: 'EXPLICIT_DENY', policyVersion, trace };
      }
      trace.push({ step: 3, name: 'Direct Grant Policy', decision: 'ALLOW', details: 'Explicit allow grant matched' });
      return { allowed: true, reason: 'EXPLICIT_ALLOW', policyVersion, trace };
    }
    trace.push({ step: 3, name: 'Direct Grant Policy', decision: 'NEUTRAL', details: 'No direct grants matched' });

    // Step 4: Role / Group Grants
    const assignedRoles = this.deps.journal.getAssignedRoles(request.subjectId, scopeKey);
    for (const role of assignedRoles) {
      const rolePerms = COMPILED_ROLE_PERMISSIONS.get(role);
      if (rolePerms && (rolePerms.has('*') || rolePerms.has(request.permission))) {
        trace.push({
          step: 4,
          name: 'Role-Based Grant',
          decision: 'ALLOW',
          details: `Role '${role}' grants permission '${request.permission}'`,
        });
        return { allowed: true, reason: 'ROLE_ALLOW', policyVersion, trace };
      }
    }
    trace.push({ step: 4, name: 'Role-Based Grant', decision: 'NEUTRAL', details: 'No role permissions matched' });

    // Step 5: Default Fallback Policy
    trace.push({ step: 5, name: 'Default Fallback Policy', decision: 'DENY', details: 'Zero-trust default deny executed' });
    return { allowed: false, reason: 'DEFAULT_DENY', policyVersion, trace };
  }

  authorizePolicyChange(
    actorId: string,
    change: PolicyChange,
    nowMs: number
  ): { allowed: boolean; reason: string } {
    // 1. Anti-Self-Escalation: Actor cannot grant permissions or roles to themselves
    if (actorId === change.subjectId && (change.action === 'ASSIGN_ROLE' || change.action === 'GRANT_PERMISSION')) {
      return { allowed: false, reason: 'Anti-escalation: self-granting permissions or roles is forbidden' };
    }

    // 2. Scope Containment Rule
    const actorScopeKey = 'system'; // System actors have system scope; room managers are contained
    if (!this.validateScopeContainment(actorScopeKey, change.scope)) {
      return { allowed: false, reason: 'Scope containment violation: actor authority scope cannot manage target scope' };
    }

    if (change.action === 'LIFT_RESTRICTION' || change.action === 'OVERRIDE_RESTRICTION') {
      const actorRoles = this.deps.journal.getAssignedRoles(actorId, formatScopeKey(change.scope));
      const hasAdminOrOwner = actorRoles.has('owner') || actorRoles.has('systemAdmin') || actorRoles.has('roomManager');
      if (!hasAdminOrOwner) {
        return {
          allowed: false,
          reason: `Privilege escalation blocked: actor '${actorId}' lacks administrative role to modify restrictions`,
        };
      }
    }

    // 3. Symbolic Capability Containment Check (Grant <= ActorCapabilities)
    if (change.permission) {
      const actorRoles = this.deps.journal.getAssignedRoles(actorId, formatScopeKey(change.scope));
      let actorHasPermission = false;
      for (const role of actorRoles) {
        const perms = COMPILED_ROLE_PERMISSIONS.get(role);
        if (perms && (perms.has('*') || perms.has(change.permission))) {
          actorHasPermission = true;
          break;
        }
      }
      if (!actorHasPermission) {
        return {
          allowed: false,
          reason: `Privilege escalation blocked: actor '${actorId}' does not possess permission '${change.permission}'`,
        };
      }
    }

    return { allowed: true, reason: 'Policy change authorized under capability containment' };
  }

  executeAuthorizedCommand<T = unknown>(
    actorId: string,
    permission: Permission,
    scope: PermissionScope,
    nowMs: number,
    action: () => T
  ): CommandResult<T> {
    const accessReq: AccessRequest = {
      meshId: this.deps.meshId,
      subjectId: actorId,
      permission,
      scope,
      nowMs,
    };

    const explanation = this.explainAccess(accessReq);
    if (!explanation.allowed) {
      const code = explanation.reason === 'UNAUTHENTICATED' ? 'UNAUTHENTICATED' : explanation.reason === 'REVOKED' ? 'REVOKED' : 'FORBIDDEN';
      return {
        ok: false,
        code,
        explanation,
      };
    }

    const data = action();
    return {
      ok: true,
      policyVersion: explanation.policyVersion,
      data,
    };
  }

  private validateScopeContainment(actorScopeKey: string, targetScope: PermissionScope): boolean {
    if (actorScopeKey === 'system') return true;
    const targetKey = formatScopeKey(targetScope);
    return targetKey.startsWith(actorScopeKey);
  }
}

export function requirePermission(
  evaluator: ScopedRbacEvaluator,
  request: AccessRequest,
  actionName: string
): void {
  const result = evaluator.evaluateAccess(request);
  if (!result.allowed) {
    throw new Error(
      `[Kernel RBAC Denial] Action '${actionName}' denied for subject '${request.subjectId}'. Reason: ${result.reason} (Step ${result.evaluatedAtStep})`
    );
  }
}
